"use client";

import { Check, Close, FeatureSearch, Search } from "@ellaandil/ui";
import { Translate } from "@/components/Translate";
import { Button } from "@/components/ui/Button";
import { Typography } from "@/components/ui/Typography";
import { useCountrySearch } from "@/features/navigation/GeoSelector/useCountrySearch";
import { useTranslations } from "@/i18n/client";

interface CurrencyViewProps {
	selectedCountry: string;
	pendingCountry: string;
	setPendingCountry: (country: string) => void;
}

export const CurrencyView = ({
	selectedCountry,
	pendingCountry,
	setPendingCountry,
}: CurrencyViewProps) => {
	const t = useTranslations("geo");
	const { query, setQuery, results } = useCountrySearch(selectedCountry);

	const showNoResults = query.trim() !== "" && results.length === 0;

	return (
		<div className="flex flex-col gap-grid-section-vertical-md">
			<Typography variant="label-sm-reg" as="p" className="leading-[1.15] text-text-sand-text-4">
				{t("changeCurrency")}
			</Typography>

			<label className="flex h-10 items-center gap-2 border-b border-border-medium py-1 text-text-dark-text-1">
				<Search className="size-5 shrink-0" />
				<input
					type="text"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					placeholder={t("searchPlaceholder")}
					aria-label={t("searchPlaceholder")}
					className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-text-sand-text-4"
				/>
				{query && (
					<button
						type="button"
						onClick={() => setQuery("")}
						aria-label={t("clearSearch")}
						className="inline-flex size-6 shrink-0 cursor-pointer items-center justify-center"
					>
						<Close className="size-3.5" />
					</button>
				)}
			</label>

			{showNoResults ? (
				<div className="flex flex-col items-center justify-center gap-grid-component-spacing-2xl py-grid-component-spacing-2xl text-center text-text-dark-text-1">
					<FeatureSearch className="size-8" />
					<div className="flex flex-col gap-grid-component-spacing-2xs">
						<Typography variant="heading-lg" as="p" className="leading-[1.25]">
							{t("noResultsTitle")}
						</Typography>
						<Typography variant="body-md-reg" as="p" className="leading-[1.5]">
							{t("noResultsBody")}
						</Typography>
					</div>
					<Button variant="solid" color="primary" size="large" onClick={() => setQuery("")}>
						{t("clearSearch")}
					</Button>
				</div>
			) : (
				<ul className="flex flex-col gap-grid-component-spacing-2xs">
					{results.map((c) => {
						const isSelected = pendingCountry === c.country;
						return (
							<li key={c.country}>
								<button
									type="button"
									onClick={() => setPendingCountry(c.country)}
									className="flex w-full cursor-pointer items-center gap-0.5 py-0.5 text-left outline-none"
								>
									<span
										className={`flex-1 font-body font-regular text-sm leading-[1.5] ${
											isSelected ? "text-text-dark-text-2" : "text-text-dark-text-1"
										}`}
									>
										<Translate>{`countries.${c.name}`}</Translate>{" "}
										<span className="text-text-dark-text-2">({c.currency})</span>
									</span>
									{isSelected && <Check className="size-5 text-text-dark-text-2" />}
								</button>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};
